<template>
  <messenger
    :title="t('components.messenger.report.title')"
    :description="t('components.messenger.report.description')"
    :back-link="route('space.problems', [tenant.slug, problem.space.id])"
    :back-link-label="location.name"
  >
    <template v-slot:content>
      <div class="flex flex-col h-full gap-4">
        <div class="flex flex-col gap-4 px-4 grow pt-4">
          <span class="text-sm font-medium text-gray-500">
            {{ t("components.messenger.report.describe_the_problem") }}
          </span>

          <Errors :errors="input?.errors" />

          <div>
            <textarea
              v-model="description"
              maxlength="200"
              rows="4"
              :placeholder="t('components.empty_states.type_here')"
              class="w-full resize-none rounded-md border border-gray-300 focus:border-gray-300 focus:ring-1 focus:ring-gray-300 px-3 py-2.5"
            />
            <div class="flex justify-end">
              <span
                class="text-sm text-gray-500 font-medium"
                :class="{ 'text-red-500': description?.length === 200 }"
                >{{ description ? 200 - description.length : "200" }} {{ " " }}
                {{ t("components.messenger.report.characters_left") }}</span
              >
            </div>
          </div>

          <!-- Media -->
          <span class="text-sm font-medium text-gray-500">
            {{ t("components.messenger.report.add_pictures") }}
          </span>
          <div class="flex space-x-2">
            <div v-for="(item, index) in media" :key="index">
              <MediaPreview :item="item" size="sm" />
            </div>
            <div
              @click="handleMediaSelectorOpened"
              v-for="index in placeholderCount"
              :key="index"
              class="w-14 h-14 flex items-center justify-center bg-gray-100 hover:cursor-pointer hover:bg-gray-200 rounded-sm"
            >
              <ImagePlus class="w-6 h-6 text-gray-600" />
            </div>
          </div>
        </div>

        <div
          class="border-t border-gray-200 p-4 flex gap-4"
          :class="{
            'justify-end': requiresContactDetails,
            'justify-between': !requiresContactDetails,
          }"
        >
          <div class="text-xs text-gray-500" v-if="!requiresContactDetails">
            <i18n-t keypath="components.terms_and_conditions.message" tag="p">
              <template v-slot:label>
                <span
                  v-text="t('components.buttons.send')"
                  class="text-lowercase"
                />
              </template>
              <template v-slot:terms>
                <a
                  href="https://www.fixform.com/terms-of-use"
                  target="_blank"
                  v-text="t('components.terms_and_conditions.terms_label')"
                  class="underline hover:text-gray-900"
                />
              </template>
              <template v-slot:privacy>
                <a
                  href="https://www.fixform.com/privacy-policy"
                  target="_blank"
                  v-text="t('components.terms_and_conditions.privacy_label')"
                  class="underline hover:text-gray-900"
                />
              </template>
            </i18n-t>
          </div>

          <primary-button
            v-if="!requiresContactDetails"
            :label="t('components.buttons.send')"
            @click="handleReportProblem"
            :loading="isLoading"
            :disabled="!description || description.length === 0"
          />
          <primary-button
            class="justify-self-end"
            v-else
            :label="t('components.buttons.send')"
            @click="handleReportProblem"
            :loading="isLoading"
            :disabled="!description || description.length === 0"
            :icon="ArrowNarrowRight"
            iconPosition="right"
          />
        </div>
      </div>

      <FileInput
        accept="image/png, image/jpg, image/jpeg, video/mp4, video/quicktime, video/ogg, video/webm"
        ref="input"
        @input="handleMediaSelected"
        hidden
      />
    </template>
  </messenger>
</template>

<script setup>
import { usePage } from "@inertiajs/vue3";
import PrimaryButton from "@/Tailwind/Components/Buttons/PrimaryButton.vue";
import { router } from "@inertiajs/vue3";
import route from "ziggy-js";
import { computed, ref } from "vue";
import ImagePlus from "@/Tailwind/Icon/ImagePlus.vue";
import Messenger from "@/Tailwind/Layouts/Messenger.vue";
import ArrowNarrowRight from "@/Tailwind/Icon/ArrowNarrowRight.vue";
import MediaPreview from "@/Tailwind/Components/MediaPreview.vue";
import { useI18n } from "vue-i18n";
import Errors from "@/Tailwind/Components/Errors.vue";
import { FileInput } from "@/Components/Inputs";

const { t } = useI18n();

const isLoading = ref(false);
const input = ref();
const description = ref();
const media = ref([]);

const problem = usePage().props.problem;
const tenant = usePage().props.tenant;
const location = usePage().props.location;

const requiresContactDetails =
  usePage().props.tenant.settings.require_contact_details;

const placeholderCount = computed(() => {
  if (!media.value) {
    return 3;
  }

  return 3 - media.value.length;
});

function handleMediaSelectorOpened() {
  input.value.click();
}

function handleMediaSelected(files) {
  if (!files || !files.length) {
    return;
  }

  const [file] = files;

  media.value.push(file);
}

function handleReportProblem() {
  router.post(
    route("problem.report", [tenant.slug, problem.id]),
    {
      description: description.value,
      photos: media.value.map((item) => item),
    },
    {
      onStart: () => (isLoading.value = true),
      onFinish: () => (isLoading.value = false),
    }
  );
}
</script>
